import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { User, Mail, Phone, FileText } from 'lucide-react';
import { useQuoteSubmit } from '../hooks/useQuoteSubmit';

const SERVICE_TYPES = ['Custom Build', 'Repair', 'Cleaning', 'Sight Installation', 'Other'];
const FIREARM_TYPES = ['Pistol', 'Rifle', 'Shotgun', 'AR Platform', 'Other'];
const NATIVE_SELECT_CLASS =
  'flex h-10 w-full items-center justify-between rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white ring-offset-background placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

const InputWithIcon = ({ icon: Icon, ...rest }) => (
  <div className="relative">
    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
    <Input {...rest} className="pl-10 bg-slate-800 border-slate-700 text-white focus:border-gray-400 focus:ring-gray-400" />
  </div>
);

const NativeSelect = ({ id, name, value, onChange, disabled, placeholder, options }) => (
  <select
    id={id} name={name} required value={value} onChange={onChange} disabled={disabled}
    className={NATIVE_SELECT_CLASS}
  >
    <option value="" disabled>{placeholder}</option>
    {options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
  </select>
);

const QuoteForm = ({ onClose }) => {
  const { formData, isSubmitting, handleInputChange, handleSubmit } = useQuoteSubmit(onClose);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="quote-name" className="text-gray-300">Full Name *</Label>
        <InputWithIcon icon={User} id="quote-name" name="name" type="text" required
          value={formData.name} onChange={handleInputChange} disabled={isSubmitting} placeholder="John Doe" />
      </div>

      <div className="space-y-2">
        <Label htmlFor="quote-email" className="text-gray-300">Email Address *</Label>
        <InputWithIcon icon={Mail} id="quote-email" name="email" type="email" required
          value={formData.email} onChange={handleInputChange} disabled={isSubmitting} placeholder="john@example.com" />
      </div>

      <div className="space-y-2">
        <Label htmlFor="quote-phone" className="text-gray-300">Phone Number *</Label>
        <InputWithIcon icon={Phone} id="quote-phone" name="phone" type="tel" required
          value={formData.phone} onChange={handleInputChange} disabled={isSubmitting} placeholder="(555) 123-4567" />
      </div>

      <div className="space-y-2">
        <Label htmlFor="quote-service-type" className="text-gray-300">Service Type *</Label>
        <NativeSelect id="quote-service-type" name="service_type"
          value={formData.service_type} onChange={handleInputChange} disabled={isSubmitting}
          placeholder="Select service type" options={SERVICE_TYPES} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="quote-firearm-type" className="text-gray-300">Firearm Type *</Label>
        <NativeSelect id="quote-firearm-type" name="firearm_type"
          value={formData.firearm_type} onChange={handleInputChange} disabled={isSubmitting}
          placeholder="Select firearm type" options={FIREARM_TYPES} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="quote-description" className="text-gray-300">Project Description *</Label>
        <div className="relative">
          <FileText className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
          <Textarea id="quote-description" name="description" required rows={5}
            value={formData.description} onChange={handleInputChange} disabled={isSubmitting}
            className="pl-10 bg-slate-800 border-slate-700 text-white focus:border-gray-400 focus:ring-gray-400 resize-none"
            placeholder="Describe your project in detail..." />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="quote-timeline" className="text-gray-300">Preferred Timeline (Optional)</Label>
        <Input id="quote-timeline" name="timeline" type="text"
          value={formData.timeline} onChange={handleInputChange} disabled={isSubmitting}
          className="bg-slate-800 border-slate-700 text-white focus:border-gray-400 focus:ring-gray-400"
          placeholder="e.g., Within 2 weeks" />
      </div>

      <Button type="submit" disabled={isSubmitting}
        className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 transition-colors duration-200">
        {isSubmitting ? 'Submitting...' : 'Request Quote'}
      </Button>
    </form>
  );
};

export default QuoteForm;
